import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import TodayIcon from "@mui/icons-material/Today";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import PlaceIcon from "@mui/icons-material/Place";
import EventIcon from "@mui/icons-material/Event";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import {
  RecordContextProvider,
  Title,
  useCreatePath,
  usePermissions,
  type RaRecord,
} from "react-admin";
import { Link as RouterLink } from "react-router-dom";

import { StatCard } from "@/components/dashboard";
import { callApi } from "@/dataprovider/misc-apis";
import { formatListDate } from "@/utils/date-format";
import { MONTH_NAMES } from "@/utils/hijri-date-utils";
import { hasPermission } from "@/utils/permission-utils";

import MiqaatRsvpDemographics from "@/containers/miqaat-rsvp/miqaats/MiqaatRsvpDemographics";

import { pickFeaturedMiqaatId } from "./pick-featured-miqaat";

type DashboardCandidate = {
  id: string;
  name: string;
  mealType: string;
  venue: string | null;
  venueIds: string[];
  defaultVenueId: string | null;
  dateMode: string;
  englishDate: string | null;
  hijriMonth: number | null;
  hijriDay: number | null;
  occurrenceDate: string;
  isToday: boolean;
  rsvpOpen: boolean;
};

type DashboardAnalytics = {
  upcomingCount: number;
  todayCount: number;
  rsvpOpenCount: number;
  attendanceScannedToday: number;
  venuesCount: number;
};

type DashboardResponse = {
  candidates: DashboardCandidate[];
  analytics: DashboardAnalytics;
};

const MEAL_LABELS: Record<string, string> = {
  LUNCH: "Lunch",
  DINNER: "Dinner",
};

function formatCandidateDate(c: DashboardCandidate): string {
  if (c.dateMode === "PERMANENT") {
    const month =
      typeof c.hijriMonth === "number" && c.hijriMonth >= 0 && c.hijriMonth < 12
        ? MONTH_NAMES.short.en[c.hijriMonth]
        : "—";
    return `Hijri ${c.hijriDay ?? "—"} · ${month} · ${formatListDate(c.occurrenceDate, { empty: "—" })}`;
  }
  return formatListDate(c.occurrenceDate || c.englishDate, { empty: "—" });
}

function mealLabel(mealType: string): string {
  return MEAL_LABELS[String(mealType).toUpperCase()] ?? String(mealType || "—");
}

type NavCardProps = {
  icon: ComponentType<SvgIconProps>;
  title: ReactNode;
  description: ReactNode;
  to: string;
  disabled?: boolean;
};

function NavCard({ icon: Icon, title, description, to, disabled }: NavCardProps) {
  return (
    <Card variant="outlined" sx={{ height: "100%", opacity: disabled ? 0.55 : 1 }}>
      <CardActionArea component={RouterLink} to={to} disabled={disabled} sx={{ height: "100%" }}>
        <CardContent
          sx={{
            display: "flex",
            flexDirection: { xs: "row", sm: "column" },
            alignItems: "center",
            gap: { xs: 1.5, sm: 0 },
            py: { xs: 2, sm: 2.5 },
          }}
        >
          <Icon sx={{ fontSize: { xs: 32, sm: 36 }, color: "primary.main", mb: { sm: 1 } }} />
          <Box sx={{ minWidth: 0, flex: 1, textAlign: { xs: "left", sm: "center" } }}>
            <Typography variant="h6" sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function CandidateChips({ candidate }: { candidate: DashboardCandidate }) {
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {candidate.isToday ? <Chip size="small" color="success" label="Today" /> : null}
      {candidate.rsvpOpen ? <Chip size="small" color="primary" label="RSVP open" /> : null}
      {!candidate.isToday && !candidate.rsvpOpen ? (
        <Chip size="small" variant="outlined" label="Upcoming" />
      ) : null}
    </Stack>
  );
}

export default function MiqaatRsvpDashboard() {
  const createPath = useCreatePath();
  const { permissions } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<DashboardResponse | null>(null);
  const [featuredOverrideId, setFeaturedOverrideId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void callApi({
      location: "miqaats",
      method: "GET",
      id: "rsvp-dashboard",
    })
      .then((res) => {
        if (cancelled) return;
        setPayload((res.data as DashboardResponse) ?? null);
        setFeaturedOverrideId(null);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load dashboard");
        setPayload(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const candidates = payload?.candidates ?? [];
  const analytics = payload?.analytics;

  const autoFeaturedId = useMemo(() => pickFeaturedMiqaatId(candidates), [candidates]);
  const featuredId =
    featuredOverrideId && candidates.some((c) => c.id === featuredOverrideId)
      ? featuredOverrideId
      : autoFeaturedId;
  const featured = candidates.find((c) => c.id === featuredId) ?? null;
  const others = candidates.filter((c) => c.id !== featuredId);

  const attendanceCreateBase = createPath({ resource: "miqaatAttendance", type: "create" });
  const miqaatsListPath = createPath({ resource: "miqaats", type: "list" });
  const venuesListPath = createPath({ resource: "miqaatVenues", type: "list" });
  const attendanceListPath = createPath({ resource: "miqaatAttendance", type: "list" });

  const canViewVenues = hasPermission(permissions, "miqaatVenues.view");
  const canViewAttendance = hasPermission(permissions, "miqaatAttendance.view");

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2, md: 3 },
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Title title="Miqaat RSVP" />
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontWeight: 600, fontSize: { xs: "1.25rem", sm: "1.5rem" } }}
      >
        Miqaat RSVP overview
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 2, sm: 2.5 } }}>
        RSVP and attendance overview
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      ) : (
        <>
          {featured ? (
            <Card variant="outlined" sx={{ borderRadius: 2, mb: { xs: 2, sm: 3 }, minWidth: 0 }}>
              <CardContent
                sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}
              >
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: 0.7, display: "block", mb: 1 }}
                >
                  Featured miqaat
                </Typography>
                <Typography
                  variant="h5"
                  component="h2"
                  fontWeight={600}
                  sx={{
                    mb: 1,
                    fontSize: { xs: "1.25rem", sm: "1.5rem" },
                    wordBreak: "break-word",
                  }}
                >
                  {featured.name}
                </Typography>
                <CandidateChips candidate={featured} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1.5, wordBreak: "break-word" }}
                >
                  {formatCandidateDate(featured)} · {mealLabel(featured.mealType)}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, mb: 2, wordBreak: "break-word" }}>
                  {featured.venue?.trim() ? featured.venue : "—"}
                </Typography>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mb: 2.5 }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    component={RouterLink}
                    to={createPath({ resource: "miqaats", type: "show", id: featured.id })}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    View details
                  </Button>
                  {canViewAttendance ? (
                    <Button
                      size="small"
                      variant="contained"
                      component={RouterLink}
                      to={`${attendanceCreateBase}?miqaatId=${encodeURIComponent(featured.id)}`}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      Mark attendance
                    </Button>
                  ) : null}
                </Stack>
                <Box sx={{ minWidth: 0, overflowX: "auto" }}>
                  <RecordContextProvider value={featured as unknown as RaRecord}>
                    <MiqaatRsvpDemographics embedded alwaysShowAttendance />
                  </RecordContextProvider>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Card variant="outlined" sx={{ borderRadius: 2, mb: { xs: 2, sm: 3 } }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  No upcoming miqaats
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create a miqaat or check past events from the Miqaats list.
                </Typography>
                <Button
                  variant="outlined"
                  component={RouterLink}
                  to={miqaatsListPath}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Open Miqaats
                </Button>
              </CardContent>
            </Card>
          )}

          <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
            <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2.4 }}>
              <StatCard
                title="Upcoming"
                value={analytics?.upcomingCount ?? 0}
                icon={EventAvailableIcon}
                valueType="count"
                color="primary"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2.4 }}>
              <StatCard
                title="Today"
                value={analytics?.todayCount ?? 0}
                icon={TodayIcon}
                valueType="count"
                color="success"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2.4 }}>
              <StatCard
                title="RSVP open"
                value={analytics?.rsvpOpenCount ?? 0}
                icon={HowToRegIcon}
                valueType="count"
                color="secondary"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2.4 }}>
              <StatCard
                title="Scanned today"
                value={analytics?.attendanceScannedToday ?? 0}
                icon={QrCodeScannerIcon}
                valueType="count"
                color="warning"
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 4, lg: 2.4 }}>
              <StatCard
                title="Venues"
                value={analytics?.venuesCount ?? 0}
                icon={PlaceIcon}
                valueType="count"
                color="primary"
              />
            </Grid>
          </Grid>

          {others.length > 0 ? (
            <Box sx={{ mb: { xs: 2, sm: 3 }, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                Other upcoming
              </Typography>
              <Grid container spacing={{ xs: 1, sm: 1.5 }}>
                {others.map((c) => (
                  <Grid key={c.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        minWidth: 0,
                        borderColor: featuredId === c.id ? "primary.main" : "divider",
                      }}
                    >
                      <CardActionArea
                        onClick={() => setFeaturedOverrideId(c.id)}
                        sx={{ height: "100%" }}
                      >
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            gutterBottom
                            sx={{ wordBreak: "break-word" }}
                          >
                            {c.name}
                          </Typography>
                          <CandidateChips candidate={c} />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1, wordBreak: "break-word" }}
                          >
                            {formatCandidateDate(c)} · {mealLabel(c.mealType)}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : null}

          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <NavCard
                icon={EventIcon}
                title="Miqaats"
                description="Create and manage events"
                to={miqaatsListPath}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <NavCard
                icon={FactCheckIcon}
                title="Attendance"
                description="Scan and review check-ins"
                to={attendanceListPath}
                disabled={!canViewAttendance}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <NavCard
                icon={PlaceIcon}
                title="Venues"
                description="Venue master list and maps"
                to={venuesListPath}
                disabled={!canViewVenues}
              />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
